
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import type {
  Map as LeafletMap,
  MarkerClusterGroup,
  Marker,
  TileLayer,
} from "leaflet";
import {
  MapPin,
  Loader2,
  List,
  Map as MapIcon,
  Heart,
  Bookmark,
  Sparkles,
  Bed,
  Bath,
  Scale,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import {
  JL_ORIGIN,
  formatPrice,
  titleCase,
  fetchMarkers as apiFetchMarkers,
  fetchListings as apiFetchListings,
  type ListingDetail,
} from "../../lib/jlApi";
import CountUp from "./CountUp";
import { readJSON, writeJSON } from "../../lib/safeStorage";
import ListingFilters from "./ListingFilters";
import {
  type Filters,
  DEFAULT_FILTERS,
  toApiFilters,
  filtersToParams,
  filtersFromParams,
} from "./filterState";
import ListingsPanel from "./ListingsPanel";
import BottomSheet, { type SnapState } from "./BottomSheet";
import CompareTray from "./CompareTray";
import {
  sortListings,
  MAX_COMPARE,
  type SortKey,
  type CompareEntry,
} from "./listingUtils";
import SavedDrawer from "./SavedDrawer";
import ListingInquiryModal, {
  type InquiryMode,
} from "./ListingInquiryModal";
import { useFavorites } from "../../hooks/useFavorites";
import { useSavedSearches } from "../../hooks/useSavedSearches";
import { useLastVisit } from "../../hooks/useLastVisit";
import { useOurListings } from "../../hooks/useOurListings";
import { trackEvent } from "../../lib/analytics";
import { Star } from "lucide-react";

// Beach-town quick-jump chips (B.2 differentiator) — flyTo targets.
const TOWN_CHIPS: { label: string; center: [number, number]; zoom: number }[] = [
  { label: "Lewes", center: [38.7745, -75.1394], zoom: 13 },
  { label: "Rehoboth", center: [38.7209, -75.076], zoom: 13 },
  { label: "Dewey", center: [38.6959, -75.0746], zoom: 14 },
  { label: "Bethany", center: [38.5395, -75.0552], zoom: 13 },
  { label: "Fenwick", center: [38.4622, -75.0513], zoom: 13 },
  { label: "Milton", center: [38.7772, -75.3097], zoom: 13 },
];

const BEACH_CENTER: [number, number] = [38.72, -75.09];
const DEFAULT_ZOOM = 12;
// Group markers within this pixel radius into one bubble. Smaller = bubbles
// break apart into individual pins at lower zoom (more pins visible sooner).
const CLUSTER_RADIUS = 70;

// A Leaflet marker with the listing price stashed on it, so cluster bubbles
// can compute their price range from their child markers.
type PricedMarker = Marker & { __price?: number };

// CARTO basemap variants for the day/dusk toggle.
const TILE_LIGHT =
  "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
const TILE_DARK =
  "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";
const TILE_ATTR =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';
const MAP_DARK_KEY = "db-map-dark";

// When a text search is active we scan this whole region (roughly Sussex
// County / the Delaware beaches) instead of just the current viewport, then
// fit the map to the matches — so a search "jumps" to results.
const REGION_BOUNDS = { swLat: 38.4, neLat: 39.0, swLng: -75.8, neLng: -75.0 };

export default function LiveListingsMap({
  initialFilters,
  initialTown,
  urlSync = true,
}: {
  // Preset/deep-link filters to seed the browser with (e.g. from /listings?p=).
  initialFilters?: Partial<Filters>;
  // Pre-center the map on a town (area pages) — same targets as the chips.
  initialTown?: string;
  // Mirror filters + center/zoom into the URL (default). Disable for embeds
  // (e.g. the home page) where map params must NOT rewrite the page URL.
  urlSync?: boolean;
} = {}) {
  const urlSyncRef = useRef(urlSync);
  urlSyncRef.current = urlSync;
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const layerRef = useRef<MarkerClusterGroup | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);
  // Set before an auto fit-to-results so the resulting `moveend` doesn't
  // trigger a redundant refetch (which would loop).
  const skipNextMoveFetchRef = useRef(false);
  // id -> leaflet marker, so a card hover/click can highlight its pin.
  const markersByIdRef = useRef<Record<number, PricedMarker>>({});
  // Basemap tile layer (swapped for the day/dusk toggle).
  const tileRef = useRef<TileLayer | null>(null);
  // Batch 7: desktop-only photo peek on pin hover.
  const hoverCapableRef = useRef(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // True only until the first successful fetch, to gate the one-time pin drop-in.
  const firstLoadRef = useRef(true);

  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Dusk map toggle (persisted). debeach default = CARTO dark_all (B.2).
  const [dark] = useState<boolean>(() => readJSON(MAP_DARK_KEY, true));
  const darkRef = useRef(dark);
  const [hoverCard, setHoverCard] = useState<{
    id: number;
    x: number;
    y: number;
  } | null>(null);

  // Hybrid list state (Batch 2).
  const [listings, setListings] = useState<ListingDetail[]>([]);
  const listingsRef = useRef<ListingDetail[]>([]);
  const [listLoading, setListLoading] = useState(false); // initial (no data yet)
  const [refetching, setRefetching] = useState(false); // background refetch
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selectedIdRef = useRef<number | null>(null);
  const [sort, setSort] = useState<SortKey>("newest");
  const [snap, setSnap] = useState<SnapState>("peek");

  // B.2 differentiator: "OUR LISTINGS" chip — isolate the team's own
  // listings (PART F feed) as gold-star pins across the whole region.
  const { listings: ourListings } = useOurListings();
  const ourMls = useMemo(
    () => new Set(ourListings.map((l) => l.mls).filter(Boolean) as string[]),
    [ourListings]
  );
  const [ourOnly, setOurOnly] = useState(false);
  const ourOnlyRef = useRef(ourOnly);
  const ourMlsRef = useRef(ourMls);
  useEffect(() => {
    ourOnlyRef.current = ourOnly;
  }, [ourOnly]);
  useEffect(() => {
    ourMlsRef.current = ourMls;
  }, [ourMls]);

  // Batch 3: favorites, saved searches, new-since-last-visit.
  const {
    isFavorite,
    toggle: toggleFavorite,
    remove: removeFavorite,
    list: favoriteList,
    count: favoriteCount,
  } = useFavorites();
  const {
    searches: savedSearches,
    save: saveSearch,
    remove: removeSavedSearch,
    count: savedSearchCount,
  } = useSavedSearches();
  const { isNew, markSeen, hasBaseline } = useLastVisit();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newSince, setNewSince] = useState(0);
  const [inquiry, setInquiry] = useState<{
    listing: ListingDetail;
    mode: InquiryMode;
  } | null>(null);
  const openInquiry = useCallback(
    (listing: ListingDetail, mode: InquiryMode) => setInquiry({ listing, mode }),
    []
  );

  // Batch 6: compare tray (max 3). Coordinates are captured from the live
  // markers when a home is added so we can compute straight-line beach distance.
  const [compare, setCompare] = useState<CompareEntry[]>([]);
  const toggleCompare = useCallback((listing: ListingDetail) => {
    setCompare((prev) => {
      if (prev.some((e) => e.listing.id === listing.id))
        return prev.filter((e) => e.listing.id !== listing.id);
      if (prev.length >= MAX_COMPARE) return prev;
      const ll = markersByIdRef.current[listing.id]?.getLatLng();
      return [...prev, { listing, lat: ll?.lat, lng: ll?.lng }];
    });
  }, []);
  const removeCompare = useCallback(
    (id: number) => setCompare((prev) => prev.filter((e) => e.listing.id !== id)),
    []
  );
  const clearCompare = useCallback(() => setCompare([]), []);
  const isComparing = useCallback(
    (id: number) => compare.some((e) => e.listing.id === id),
    [compare]
  );
  const compareFull = compare.length >= MAX_COMPARE;

  // Compare promotion: the toolbar pill opens the tray dialog when 2+ homes
  // are picked; otherwise it teaches how to add homes via a dismissable tip.
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareHint, setCompareHint] = useState<string | null>(null);
  const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleCompareClick = useCallback(() => {
    if (compare.length >= 2) {
      setCompareOpen(true);
      return;
    }
    setCompareHint(
      compare.length === 1
        ? "One home added \u2014 tap the scale button on another home\u2019s photo to see them side-by-side."
        : "Tap the scale button on any home\u2019s photo to add it \u2014 compare up to 3 homes side-by-side."
    );
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    hintTimerRef.current = setTimeout(() => setCompareHint(null), 5500);
  }, [compare.length]);

  // Seed filters from the URL query string on first render (shareable links),
  // then layer any preset/deep-link filters on top (e.g. /listings?p=condos).
  const [filters, setFilters] = useState<Filters>(() => {
    const base =
      typeof window !== "undefined" && urlSync
        ? filtersFromParams(new URLSearchParams(window.location.search))
        : DEFAULT_FILTERS;
    return initialFilters ? { ...base, ...initialFilters } : base;
  });
  const filtersRef = useRef<Filters>(filters);
  const searching = filters.search.trim() !== "";

  // The DOM element of the currently-highlighted price pin.
  const activePinRef = useRef<HTMLElement | null>(null);

  const highlightPin = useCallback((el: HTMLElement | null) => {
    activePinRef.current?.classList.remove("jl-pin-active");
    activePinRef.current = el;
    el?.classList.add("jl-pin-active");
  }, []);

  // Mirror filters + map center/zoom into the URL query string so the current
  // view is shareable and survives a hard reload.
  const syncUrl = useCallback(() => {
    if (typeof window === "undefined" || !urlSyncRef.current) return;
    const params = new URLSearchParams(window.location.search);
    filtersToParams(filtersRef.current, params);
    const map = mapRef.current;
    if (map) {
      const c = map.getCenter();
      params.set("lat", c.lat.toFixed(4));
      params.set("lng", c.lng.toFixed(4));
      params.set("z", String(map.getZoom()));
    }
    const qs = params.toString();
    window.history.replaceState(
      null,
      "",
      qs ? `?${qs}` : window.location.pathname
    );
  }, []);

  // Highlight the pin belonging to a listing id (looked up in markersByIdRef).
  const highlightPinById = useCallback(
    (id: number | null) => {
      if (id == null) {
        highlightPin(null);
        return;
      }
      const m = markersByIdRef.current[id];
      highlightPin(m?.getElement() ?? null);
    },
    [highlightPin]
  );

  // Select a listing (card click or pin click): sync the highlighted pin and,
  // on mobile, nudge the sheet up from peek so the card is visible.
  const selectListing = useCallback(
    (id: number | null) => {
      selectedIdRef.current = id;
      setSelectedId(id);
      highlightPinById(id);
      if (id != null) setSnap((s) => (s === "peek" ? "half" : s));
      if (id != null) trackEvent("listing_view", { listing_id: id });
    },
    [highlightPinById]
  );

  // Card hover: temporarily highlight the hovered pin; on leave, restore the
  // selected pin's highlight.
  const hoverListing = useCallback(
    (id: number | null) => {
      highlightPinById(id ?? selectedIdRef.current);
    },
    [highlightPinById]
  );

  // Fetch pins (for the map + count) and full listing cards (for the list) for
  // the current viewport/region in parallel, sharing one stale-response guard.
  const fetchView = useCallback(
    async (opts?: { fit?: boolean }) => {
      const map = mapRef.current;
      const layer = layerRef.current;
      if (!map || !layer) return;

      const f = filtersRef.current;
      // A text search (or the OUR LISTINGS isolate) scans the whole region so
      // results aren't hidden off-screen; otherwise only the current viewport.
      const region = ourOnlyRef.current || f.search.trim() !== "";
      const b = map.getBounds();
      const bounds = region
        ? REGION_BOUNDS
        : {
            swLat: b.getSouth(),
            neLat: b.getNorth(),
            swLng: b.getWest(),
            neLng: b.getEast(),
          };

      const reqId = ++reqIdRef.current;
      setLoading(true);
      setError(false);
      // Keep prior results visible (dimmed) during a refetch; only show the
      // skeleton when we have nothing yet.
      if (listingsRef.current.length === 0) setListLoading(true);
      else setRefetching(true);

      try {
        const apiFilters = toApiFilters(f);
        let places: Awaited<ReturnType<typeof apiFetchMarkers>>;
        let items: Awaited<ReturnType<typeof apiFetchListings>>;
        if (ourOnlyRef.current) {
          // OUR LISTINGS isolate: the proxy caps listing cards per query, so a
          // region fetch can't be filtered down to the team's MLS#s — query
          // each MLS# directly instead (search=<MLS#>).
          const mlsList = [...ourMlsRef.current];
          const [markerSets, itemSets] = await Promise.all([
            Promise.all(
              mlsList.map((mls) =>
                apiFetchMarkers(REGION_BOUNDS, { search: mls }).catch(() => [])
              )
            ),
            Promise.all(
              mlsList.map((mls) =>
                apiFetchListings(REGION_BOUNDS, { search: mls }).catch(() => [])
              )
            ),
          ]);
          const seen = new Set<number>();
          places = markerSets.flat().filter((p) => !seen.has(p.id) && seen.add(p.id));
          const seenItems = new Set<number>();
          items = itemSets.flat().filter((i) => !seenItems.has(i.id) && seenItems.add(i.id));
        } else {
          [places, items] = await Promise.all([
            apiFetchMarkers(bounds, apiFilters),
            apiFetchListings(bounds, apiFilters),
          ]);
        }
        if (reqId !== reqIdRef.current) return; // stale response

        const L = (await import("leaflet")).default;
        layer.clearLayers();
        activePinRef.current = null;
        markersByIdRef.current = {};

        // Build every marker (no cap) and let the cluster group group them into
        // count + price-range bubbles that split apart as the user zooms in.
        const soon = f.status === "Coming Soon"; // spotlight all pins as such
        const drop = firstLoadRef.current; // one-time drop-in on first load only
        const ours = ourOnlyRef.current; // gold-star team pins
        const markers: PricedMarker[] = [];
        let newCount = 0;
        places.forEach((place) => {
          if (!Number.isFinite(place.lat) || !Number.isFinite(place.lng)) return;
          const fresh = isNew(place.id);
          if (fresh) newCount++;
          const icon = L.divIcon({
            className: `jl-price-pin${fresh ? " jl-pin-new" : ""}${
              soon ? " jl-pin-soon" : ""
            }${ours ? " jl-pin-ours" : ""}${drop ? " jl-pin-drop" : ""}`,
            html: `<span>${ours ? "\u2605 " : ""}${formatPrice(place.price)}</span>`,
            iconSize: [58, 30],
            iconAnchor: [29, 30],
          });
          const m = L.marker([place.lat, place.lng], {
            icon,
            riseOnHover: true,
          }) as PricedMarker;
          m.__price = place.price;
          m.on("click", () => selectListing(place.id));
          // Desktop-only photo peek: after ~250ms hover, show a floating card
          // (photo + price) using the listings already in memory — no fetch.
          if (hoverCapableRef.current) {
            m.on("mouseover", () => {
              if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
              hoverTimerRef.current = setTimeout(() => {
                const pt = map.latLngToContainerPoint(m.getLatLng());
                setHoverCard({ id: place.id, x: pt.x, y: pt.y });
              }, 250);
            });
            m.on("mouseout", () => {
              if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
              setHoverCard(null);
            });
          }
          markersByIdRef.current[place.id] = m;
          markers.push(m);
        });
        layer.addLayers(markers);
        firstLoadRef.current = false;
        setNewSince(newCount);
        markSeen(places.map((p) => p.id));

        // On an explicit search, zoom/pan the map to the matches.
        if (opts?.fit && markers.length) {
          skipNextMoveFetchRef.current = true;
          map.fitBounds(layer.getBounds().pad(0.15), { maxZoom: 15 });
          setTimeout(() => {
            skipNextMoveFetchRef.current = false;
          }, 900);
        }

        setCount(places.length);
        setListings(items);
        listingsRef.current = items;
      } catch {
        if (reqId === reqIdRef.current) setError(true);
      } finally {
        if (reqId === reqIdRef.current) {
          setLoading(false);
          setListLoading(false);
          setRefetching(false);
        }
      }
    },
    [selectListing, isNew, markSeen]
  );

  const scheduleFetch = useCallback(() => {
    syncUrl();
    if (skipNextMoveFetchRef.current) {
      skipNextMoveFetchRef.current = false;
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchView(), 350);
  }, [fetchView, syncUrl]);

  // Re-run the search whenever a filter changes. Debounced so typing in the
  // search box doesn't fire a request on every keystroke.
  useEffect(() => {
    filtersRef.current = filters;
    if (!mapRef.current) return; // map not ready yet; initial load handles it
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    filterDebounceRef.current = setTimeout(() => {
      fetchView({ fit: filters.search.trim() !== "" });
      syncUrl();
    }, 300);
    return () => {
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    };
  }, [filters, fetchView, syncUrl]);

  const resetFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  // Refetch (region-wide, fit-to-results) when the OUR LISTINGS isolate flips.
  useEffect(() => {
    if (!mapRef.current) return; // initial load handles it
    fetchView({ fit: ourOnly });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ourOnly]);

  // Beach-town quick-jump: flyTo the town, let the moveend handler refetch.
  const flyToTown = useCallback((center: [number, number], zoom: number) => {
    mapRef.current?.flyTo(center, zoom, { duration: 0.8 });
  }, []);

  // Count homes for a candidate filter set (mobile "Apply · Show N homes").
  // Uses the current viewport, or the whole region when a text search is set.
  const getCount = useCallback(
    async (candidate: Filters): Promise<number | null> => {
      const map = mapRef.current;
      if (!map) return null;
      const region = candidate.search.trim() !== "";
      const b = map.getBounds();
      const bounds = region
        ? REGION_BOUNDS
        : {
            swLat: b.getSouth(),
            neLat: b.getNorth(),
            swLng: b.getWest(),
            neLng: b.getEast(),
          };
      try {
        const places = await apiFetchMarkers(bounds, toApiFilters(candidate));
        return places.length;
      } catch {
        return null;
      }
    },
    []
  );

  // Serialize the current filters + map view to a query string (for saving).
  const currentParams = useCallback((): string => {
    const params = new URLSearchParams();
    filtersToParams(filtersRef.current, params);
    const map = mapRef.current;
    if (map) {
      const c = map.getCenter();
      params.set("lat", c.lat.toFixed(4));
      params.set("lng", c.lng.toFixed(4));
      params.set("z", String(map.getZoom()));
    }
    return params.toString();
  }, []);

  const handleSaveSearch = useCallback(() => {
    if (typeof window === "undefined") return;
    const name = window.prompt(
      "Name this search (e.g. \u201CLewes 3bd under $700K\u201D)"
    );
    if (name && name.trim()) saveSearch(name, currentParams());
  }, [saveSearch, currentParams]);

  // Restore a saved search: re-apply its filters + map view.
  const restoreSearch = useCallback((params: string) => {
    const sp = new URLSearchParams(params);
    setFilters(filtersFromParams(sp));
    const map = mapRef.current;
    if (map && sp.has("lat") && sp.has("lng")) {
      const lat = Number(sp.get("lat"));
      const lng = Number(sp.get("lng"));
      const z = Number(sp.get("z"));
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        map.setView([lat, lng], sp.has("z") && Number.isFinite(z) ? z : map.getZoom());
      }
    }
    setDrawerOpen(false);
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Photo peek is desktop-only: require a fine pointer that can truly hover.
    hoverCapableRef.current =
      typeof window !== "undefined" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;

    (async () => {
      const L = (await import("leaflet")).default;
      await import("leaflet.markercluster");
      if (cancelled || !containerRef.current || mapRef.current) return;

      // Restore map center/zoom from the URL if present (shareable links).
      const urlParams = new URLSearchParams(window.location.search);
      const urlLat = Number(urlParams.get("lat"));
      const urlLng = Number(urlParams.get("lng"));
      const urlZoom = Number(urlParams.get("z"));
      const hasView =
        Number.isFinite(urlLat) &&
        Number.isFinite(urlLng) &&
        urlParams.has("lat") &&
        urlParams.has("lng");

      // Town quick-jump via ?town=<label> (Polish Plan 6b) — reuses the chip
      // targets so /listings?town=lewes deep-links from the town cards work.
      const townParam = (urlParams.get("town") ?? initialTown)?.toLowerCase();
      const townChip = townParam
        ? TOWN_CHIPS.find(
            (t) =>
              t.label.toLowerCase() === townParam ||
              t.label.toLowerCase().startsWith(townParam)
          )
        : undefined;

      const map = L.map(containerRef.current, {
        center: hasView ? [urlLat, urlLng] : townChip ? townChip.center : BEACH_CENTER,
        zoom: hasView && urlParams.has("z") ? urlZoom : townChip ? townChip.zoom : DEFAULT_ZOOM,
        scrollWheelZoom: true,
        attributionControl: true,
      });
      map.attributionControl.setPrefix(false);
      tileRef.current = L.tileLayer(darkRef.current ? TILE_DARK : TILE_LIGHT, {
        maxZoom: 19,
        attribution: TILE_ATTR,
      }).addTo(map);

      const layer = L.markerClusterGroup({
        maxClusterRadius: CLUSTER_RADIUS,
        showCoverageOnHover: false,
        spiderfyOnMaxZoom: true,
        chunkedLoading: true,
        iconCreateFunction: (cluster) => {
          const childCount = cluster.getChildCount();
          let min = Infinity;
          let max = -Infinity;
          cluster.getAllChildMarkers().forEach((child) => {
            const p = (child as PricedMarker).__price;
            if (typeof p === "number" && Number.isFinite(p) && p > 0) {
              if (p < min) min = p;
              if (p > max) max = p;
            }
          });
          const range =
            min === Infinity
              ? ""
              : min === max
                ? formatPrice(min)
                : `${formatPrice(min)}\u2013${formatPrice(max)}`;
          const size = childCount < 50 ? 34 : childCount < 200 ? 40 : 46;
          return L.divIcon({
            className: "jl-cluster",
            html: `<div class="jl-cluster-inner"><span class="jl-cluster-count">${childCount}</span>${
              range ? `<span class="jl-cluster-price">${range}</span>` : ""
            }</div>`,
            iconSize: [size, size],
            iconAnchor: [size / 2, size / 2],
          }); // inner pill auto-sizes; see .jl-cluster-inner CSS
        },
      }).addTo(map);
      mapRef.current = map;
      layerRef.current = layer;

      map.on("moveend", scheduleFetch);
      fetchView();
    })();

    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
      if (mapRef.current) {
        mapRef.current.off("moveend", scheduleFetch);
        mapRef.current.remove();
        mapRef.current = null;
        layerRef.current = null;
        tileRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Swap the basemap when the dusk toggle changes; persist the choice.
  // We add the new tile layer ON TOP and only remove the old one once the new
  // tiles have loaded — `setUrl` alone doesn't re-request tiles when the map
  // hasn't moved, which left the terrain blank until a reload.
  useEffect(() => {
    darkRef.current = dark;
    writeJSON(MAP_DARK_KEY, dark);
    const map = mapRef.current;
    if (!map) return;
    let cancelled = false;
    (async () => {
      const L = (await import("leaflet")).default;
      if (cancelled || !mapRef.current) return;
      const oldTile = tileRef.current;
      const newTile = L.tileLayer(dark ? TILE_DARK : TILE_LIGHT, {
        maxZoom: 19,
        attribution: TILE_ATTR,
      }).addTo(map);
      tileRef.current = newTile;
      const dropOld = () => {
        if (oldTile) map.removeLayer(oldTile);
      };
      // Remove the old layer once the new one paints; fall back on a timeout so
      // it's never orphaned if the `load` event doesn't fire (e.g. all cached).
      newTile.once("load", dropOld);
      setTimeout(dropOld, 1200);
    })();
    return () => {
      cancelled = true;
    };
  }, [dark]);

  const sortedListings = useMemo(
    () => sortListings(listings, sort),
    [listings, sort]
  );
  const comingSoon = filters.status === "Coming Soon";
  // Quick-filter chip: spotlight Coming Soon homes (toggles back to Active).
  const toggleComingSoon = useCallback(
    () =>
      setFilters((f) => ({
        ...f,
        status: f.status === "Coming Soon" ? "Active" : "Coming Soon",
      })),
    []
  );
  const countLabel =
    count === null
      ? "Homes in view"
      : ourOnly
        ? `${count.toLocaleString()} team ${count === 1 ? "listing" : "listings"}`
        : searching
          ? `${count.toLocaleString()} ${count === 1 ? "match" : "matches"}`
          : `${count.toLocaleString()} ${count === 1 ? "home" : "homes"} in view`;
  // The listing (already in memory) backing the desktop hover photo-peek card.
  const hoverListingData = hoverCard
    ? listings.find((l) => l.id === hoverCard.id) ?? null
    : null;

  const listingsPanel = (className: string) => (
    <ListingsPanel
      listings={sortedListings}
      totalCount={count}
      loading={listLoading}
      refetching={refetching}
      error={error}
      selectedId={selectedId}
      comingSoon={comingSoon}
      sort={sort}
      onSortChange={setSort}
      onSelect={selectListing}
      onHover={hoverListing}
      isFavorite={isFavorite}
      onToggleFavorite={toggleFavorite}
      onInquire={openInquiry}
      isComparing={isComparing}
      compareFull={compareFull}
      onToggleCompare={toggleCompare}
      className={className}
    />
  );

  const savedCount = favoriteCount + savedSearchCount;

  return (
    <>
      <style>{`
        .jl-price-pin {
          background: transparent; border: 0;
          display: flex; align-items: flex-end; justify-content: center;
        }
        .jl-price-pin span {
          position: relative;
          margin-bottom: 5px; /* room for the tail; tip lands on the anchor */
          display: inline-flex; align-items: center; justify-content: center;
          padding: 4px 9px; border-radius: 9999px;
          background: var(--navy, #1e3a5f); color: #fff;
          font-size: 11px; font-weight: 600; line-height: 1;
          white-space: nowrap; box-shadow: 0 2px 8px rgba(0,0,0,.35);
          border: 1.5px solid #fff; cursor: pointer;
          transition: background .15s ease, transform .15s ease;
        }
        .jl-price-pin span::after {
          content: ""; position: absolute; left: 50%; top: 100%;
          transform: translateX(-50%);
          border: 5px solid transparent;
          border-top-color: var(--navy, #1e3a5f);
          transition: border-top-color .15s ease;
        }
        .jl-price-pin span:hover,
        .jl-price-pin.jl-pin-active span {
          background: var(--gold, #c5a572); transform: translateY(-1px);
        }
        .jl-price-pin span:hover::after,
        .jl-price-pin.jl-pin-active span::after {
          border-top-color: var(--gold, #c5a572);
        }
        .jl-price-pin.jl-pin-active { z-index: 1000 !important; }
        .leaflet-container { font-family: inherit; }

        /* Mobile: hide the Leaflet zoom control (pinch-to-zoom works on touch)
           so it doesn't sit under the Map/List toggle at top-left. Desktop
           (lg+) keeps the zoom buttons. */
        @media (max-width: 1023px) {
          .leaflet-control-zoom { display: none !important; }
        }

        /* New-since-last-visit pins get a one-time gold pulse. */
        .jl-price-pin.jl-pin-new span {
          background: var(--gold, #c5a572);
          box-shadow: 0 0 0 0 rgba(197,165,114,.7);
          animation: jl-pin-pulse 1.6s ease-out 3;
        }
        .jl-price-pin.jl-pin-new span::after { border-top-color: var(--gold, #c5a572); }
        @keyframes jl-pin-pulse {
          0% { box-shadow: 0 0 0 0 rgba(197,165,114,.7); }
          70% { box-shadow: 0 0 0 12px rgba(197,165,114,0); }
          100% { box-shadow: 0 0 0 0 rgba(197,165,114,0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .jl-price-pin.jl-pin-new span { animation: none; }
        }

        .jl-cluster { background: transparent; border: 0; overflow: visible; }
        /* Auto-width pill centered on the cluster point, so the price range
           text never spills outside its background (fixed circle was too
           narrow for ranges like "$2.2M–$2.6M"). */
        .jl-cluster-inner {
          position: absolute; left: 50%; top: 50%;
          transform: translate(-50%, -50%);
          display: inline-flex; align-items: center; gap: 5px;
          padding: 4px 10px 4px 5px; border-radius: 9999px;
          background: var(--navy, #1e3a5f); color: #fff;
          border: 2px solid #fff;
          box-shadow: 0 3px 10px rgba(0,0,0,.35);
          line-height: 1; white-space: nowrap; cursor: pointer;
          transition: background .15s ease, transform .15s ease;
        }
        .jl-cluster-inner:hover {
          background: var(--gold, #c5a572);
          transform: translate(-50%, -50%) scale(1.06);
        }
        .jl-cluster-count {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 22px; height: 22px; padding: 0 5px;
          border-radius: 9999px; background: rgba(255,255,255,.2);
          font-size: 12px; font-weight: 700;
        }
        .jl-cluster-price {
          font-size: 11px; font-weight: 600;
        }

        /* Price pins + clusters speak Barlow Condensed like the rest of the site. */
        .jl-price-pin span, .jl-cluster-inner {
          font-family: 'Barlow Condensed', sans-serif;
          letter-spacing: 0.04em;
        }

        /* OUR LISTINGS isolate: gold star pins, always on top (B.2). */
        .jl-price-pin.jl-pin-ours { z-index: 900 !important; }
        .jl-price-pin.jl-pin-ours span {
          background: var(--gold, #c9a962); color: var(--navy, #0b1520);
          border-color: #fff; font-weight: 700;
          box-shadow: 0 2px 12px rgba(201,169,98,.55);
        }
        .jl-price-pin.jl-pin-ours span::after { border-top-color: var(--gold, #c9a962); }

        /* Coming Soon spotlight: distinct gold pin with a soft steady glow. */
        .jl-price-pin.jl-pin-soon span {
          background: var(--gold, #c5a572); color: #1e3a5f;
          box-shadow: 0 0 0 0 rgba(197,165,114,.6);
          animation: jl-soon-glow 2s ease-in-out infinite;
        }
        .jl-price-pin.jl-pin-soon span::after { border-top-color: var(--gold, #c5a572); }
        @keyframes jl-soon-glow {
          0%,100% { box-shadow: 0 0 0 0 rgba(197,165,114,.5); }
          50% { box-shadow: 0 0 0 7px rgba(197,165,114,0); }
        }

        /* One-time pin drop-in on first load (root transform; composes with the
           span pulse so a new+drop pin still works). */
        .jl-price-pin.jl-pin-drop { animation: jl-pin-drop .45s cubic-bezier(.2,.7,.3,1) both; }
        @keyframes jl-pin-drop {
          0% { opacity: 0; transform: translateY(-10px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        /* Dusk basemap: retune pins/clusters for contrast on the dark tiles. */
        .jl-map-dark .jl-price-pin span {
          background: var(--gold, #c5a572); color: #12263a;
          border-color: rgba(255,255,255,.85);
        }
        .jl-map-dark .jl-price-pin span::after { border-top-color: var(--gold, #c5a572); }
        .jl-map-dark .jl-price-pin span:hover,
        .jl-map-dark .jl-price-pin.jl-pin-active span {
          background: #fff; color: #12263a;
        }
        .jl-map-dark .jl-price-pin span:hover::after,
        .jl-map-dark .jl-price-pin.jl-pin-active span::after { border-top-color: #fff; }
        .jl-map-dark .jl-cluster-inner {
          background: var(--gold, #c5a572); color: #12263a;
          border-color: rgba(255,255,255,.85);
        }
        .jl-map-dark .jl-cluster-count { background: rgba(18,38,58,.22); }

        @media (prefers-reduced-motion: reduce) {
          .jl-price-pin.jl-pin-soon span { animation: none; }
          .jl-price-pin.jl-pin-drop { animation: none; }
        }

        /* "New Listings" chip + badge: shimmer sweep and a gentle sparkle pulse. */
        .jl-new-chip::after {
          content: ""; position: absolute; inset: 0;
          background: linear-gradient(105deg, transparent 40%, rgba(255,255,255,.5) 50%, transparent 60%);
          transform: translateX(-100%);
          animation: jl-shimmer 3s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes jl-shimmer {
          0% { transform: translateX(-100%); }
          55%, 100% { transform: translateX(100%); }
        }
        .jl-sparkle { animation: jl-sparkle-pulse 2.2s ease-in-out infinite; transform-origin: center; }
        @keyframes jl-sparkle-pulse {
          0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
          50% { transform: scale(1.25) rotate(12deg); opacity: .85; }
        }
        @media (prefers-reduced-motion: reduce) {
          .jl-new-chip::after, .jl-sparkle { animation: none; }
        }
      `}</style>

      <div className="relative w-full overflow-hidden rounded-2xl shadow-lg bg-white">
        <ListingFilters
          filters={filters}
          onChange={setFilters}
          onReset={resetFilters}
          getCount={getCount}
        />

        {/* Beach-town quick-jump chips (B.2) */}
        <div className="flex items-center gap-1.5 overflow-x-auto border-b border-[var(--sand)] bg-[var(--navy)] px-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <span className="mr-1 flex-shrink-0 font-display text-[11px] font-medium uppercase tracking-[0.2em] text-white/50">
            Towns
          </span>
          {TOWN_CHIPS.map((t) => (
            <button
              key={t.label}
              onClick={() => flyToTown(t.center, t.zoom)}
              className="flex-shrink-0 border border-white/20 px-3 py-1.5 font-display text-[11px] font-medium uppercase tracking-[0.15em] text-white/85 transition hover:border-[var(--gold)] hover:text-[var(--gold)] active:scale-95"
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Save-search / Saved controls + Our Listings + New Listings + Compare */}
        <div className="flex items-center gap-2 overflow-x-auto border-b border-[var(--sand)] bg-gradient-to-r from-white via-[var(--cream)]/70 to-white px-3 py-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <button
            onClick={() => setOurOnly((v) => !v)}
            aria-pressed={ourOnly}
            className={`relative inline-flex min-h-[40px] flex-shrink-0 items-center gap-1.5 border px-3.5 font-display text-[12px] font-semibold uppercase tracking-[0.15em] transition-all duration-300 active:scale-95 ${
              ourOnly
                ? "border-transparent bg-[var(--gold)] text-[var(--navy)] shadow-[0_2px_14px_rgba(201,169,98,.55)]"
                : "border-[var(--gold)]/50 bg-white text-[var(--navy)] hover:border-[var(--gold)] hover:shadow-[0_2px_10px_rgba(201,169,98,.3)]"
            }`}
          >
            <Star
              size={15}
              className={ourOnly ? "fill-[var(--navy)] text-[var(--navy)]" : "text-[var(--gold)]"}
            />
            Our Listings
          </button>
          <button
            onClick={toggleComingSoon}
            aria-pressed={comingSoon}
            className={`jl-new-chip relative inline-flex min-h-[40px] flex-shrink-0 items-center gap-1.5 overflow-hidden rounded-full border px-3.5 text-sm font-semibold transition-all duration-300 active:scale-95 ${
              comingSoon
                ? "border-transparent bg-gradient-to-r from-[var(--gold)] via-[#d9bf8d] to-[var(--gold)] text-white shadow-[0_2px_14px_rgba(197,165,114,.55)]"
                : "border-[var(--gold)]/50 bg-white text-[var(--navy)] hover:border-[var(--gold)] hover:shadow-[0_2px_10px_rgba(197,165,114,.3)]"
            }`}
          >
            <Sparkles
              size={15}
              className={`jl-sparkle ${comingSoon ? "text-white" : "text-[var(--gold)]"}`}
            />
            New Listings
          </button>
          <button
            onClick={handleCompareClick}
            aria-pressed={compare.length > 0}
            className={`relative inline-flex min-h-[40px] flex-shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-sm font-semibold transition-all duration-300 active:scale-95 ${
              compare.length > 0
                ? "border-[var(--navy)] bg-[var(--navy)] text-white shadow-md"
                : "border-[var(--dune)] bg-white text-[var(--navy)] hover:border-[var(--navy)] hover:shadow-sm"
            }`}
          >
            <Scale
              size={15}
              className={compare.length > 0 ? "text-[var(--gold)]" : "text-[var(--slate-blue)]"}
            />
            Compare
            <AnimatePresence>
              {compare.length > 0 && (
                <motion.span
                  key={compare.length}
                  initial={{ scale: 0.4, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.4, opacity: 0 }}
                  className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[var(--gold)] px-1 text-xs font-bold text-white"
                >
                  {compare.length}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
          <button
            onClick={handleSaveSearch}
            className="ml-auto inline-flex min-h-[40px] flex-shrink-0 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-[var(--navy)] hover:bg-[var(--sand)] active:scale-95"
          >
            <Bookmark size={15} />
            <span className="hidden sm:inline">Save this search</span>
            <span className="sm:hidden">Save</span>
          </button>
          <button
            onClick={() => setDrawerOpen(true)}
            className="relative inline-flex min-h-[40px] flex-shrink-0 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-[var(--navy)] hover:bg-[var(--sand)] active:scale-95"
          >
            <Heart size={15} className="text-[var(--gold)]" /> Saved
            {savedCount > 0 && (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[var(--gold)] px-1 text-xs font-semibold text-white">
                {savedCount}
              </span>
            )}
          </button>
        </div>

        {/* Compare how-to tip (auto-dismisses) */}
        <AnimatePresence>
          {compareHint && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden border-b border-[var(--gold)]/30 bg-gradient-to-r from-[var(--cream)] to-white"
            >
              <p className="flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-[var(--navy)]">
                <Scale size={14} className="flex-shrink-0 text-[var(--gold)]" />
                {compareHint}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="relative flex flex-col lg:grid lg:grid-cols-3 lg:gap-0">
          {/* Map area (also hosts the mobile bottom sheet + Map/List toggle) */}
          <div className="relative order-1 h-[80vh] min-h-[540px] lg:col-span-2 lg:h-[min(75vh,680px)]">
            <div
              ref={containerRef}
              className={`absolute inset-0 z-0 ${dark ? "jl-map-dark" : ""}`}
            />

            {/* Status pill (top-right so it never overlaps the zoom control) */}
            <div className="absolute top-3 right-3 z-[500] flex items-center gap-2 rounded-full border border-black/5 bg-white/95 backdrop-blur px-3 py-1.5 shadow-md text-xs font-medium text-[var(--navy)]">
              {loading ? (
                <>
                  <Loader2 size={13} className="animate-spin" />
                  Loading…
                </>
              ) : error ? (
                <span className="text-red-600">Couldn&apos;t load listings</span>
              ) : count === null ? (
                <>
                  <MapPin size={13} className="text-[var(--gold)]" />
                  Move the map to load homes
                </>
              ) : (
                <>
                  <MapPin size={13} className="text-[var(--gold)]" />
                  <CountUp value={count} className="font-semibold" />{" "}
                  {searching
                    ? count === 1
                      ? "match"
                      : "matches"
                    : count === 1
                      ? "home in view"
                      : "homes in view"}
                </>
              )}
            </div>

            {/* Desktop-only photo peek on pin hover (data already in memory) */}
            {hoverCard && hoverListingData && (
              <div
                className="pointer-events-none absolute z-[600] hidden w-44 -translate-x-1/2 -translate-y-full overflow-hidden rounded-xl border border-[var(--sand)] bg-white shadow-xl lg:block"
                style={{ left: hoverCard.x, top: hoverCard.y - 38 }}
              >
                {hoverListingData.imageUrl ? (
                  <img
                    src={`${JL_ORIGIN}${hoverListingData.imageUrl}`}
                    alt=""
                    className="h-24 w-full object-cover"
                  />
                ) : (
                  <div className="flex h-24 w-full items-center justify-center bg-[var(--sand)]">
                    <MapPin size={22} className="text-[var(--dune)]" />
                  </div>
                )}
                <div className="p-2">
                  <p className="text-sm font-semibold text-[var(--navy)]">
                    {hoverListingData.priceDisplay ||
                      formatPrice(hoverListingData.price)}
                  </p>
                  <p className="truncate text-xs text-[var(--slate)]">
                    {titleCase(hoverListingData.street)}
                  </p>
                  <div className="mt-1 flex items-center gap-2.5 text-[11px] text-[var(--slate)]">
                    {hoverListingData.bed && (
                      <span className="flex items-center gap-0.5">
                        <Bed size={11} /> {hoverListingData.bed}
                      </span>
                    )}
                    {hoverListingData.bath && (
                      <span className="flex items-center gap-0.5">
                        <Bath size={11} /> {hoverListingData.bath}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* New-since-last-visit chip */}
            {hasBaseline && newSince > 0 && (
              <div className="absolute left-1/2 top-14 lg:top-3 z-[500] flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-[var(--gold)] px-3 py-1.5 text-xs font-semibold text-white shadow-md">
                <Sparkles size={13} />
                {newSince} new since your last visit
              </div>
            )}

            {/* Mobile Map / List toggle */}
            <div className="absolute left-3 top-3 z-[500] flex overflow-hidden rounded-full bg-white/95 text-xs font-medium shadow-md backdrop-blur lg:hidden">
              <button
                onClick={() => setSnap("peek")}
                className={`flex items-center gap-1 px-3 py-1.5 transition ${
                  snap === "peek"
                    ? "bg-[var(--navy)] text-white"
                    : "text-[var(--navy)]"
                }`}
              >
                <MapIcon size={13} /> Map
              </button>
              <button
                onClick={() => setSnap("full")}
                className={`flex items-center gap-1 px-3 py-1.5 transition ${
                  snap !== "peek"
                    ? "bg-[var(--navy)] text-white"
                    : "text-[var(--navy)]"
                }`}
              >
                <List size={13} /> List
              </button>
            </div>

            {/* Mobile bottom sheet: the list IS the sheet (contained in map area) */}
            <BottomSheet
              snap={snap}
              onSnapChange={setSnap}
              peek={
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 font-semibold text-[var(--navy)]">
                    <span
                      className={`h-2 w-2 flex-shrink-0 rounded-full bg-[var(--gold)] ${
                        loading ? "animate-pulse" : ""
                      }`}
                    />
                    {count === null ? "Homes in view" : countLabel}
                  </span>
                  <span className="flex items-center gap-1 text-xs font-medium text-[var(--slate)]">
                    {snap === "peek" ? (
                      <>
                        <ChevronUp size={14} className="text-[var(--gold)]" />
                        Swipe up for list
                      </>
                    ) : (
                      <>
                        <ChevronDown size={14} className="text-[var(--gold)]" />
                        Swipe down for map
                      </>
                    )}
                  </span>
                </div>
              }
            >
              {listingsPanel("h-full")}
            </BottomSheet>

            {/* Compare tray: docked above the sheet peek (mobile) / bottom-right
                (desktop), within the map section so it never covers the
                viewport-fixed MobileContactBar. */}
            <CompareTray
              entries={compare}
              onRemove={removeCompare}
              onClear={clearCompare}
              open={compareOpen}
              onOpenChange={setCompareOpen}
            />
          </div>

          {/* Desktop list column */}
          <div className="order-2 hidden h-[min(75vh,680px)] lg:flex">
            {listingsPanel("w-full")}
          </div>
        </div>
      </div>

      <SavedDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        favorites={favoriteList}
        onRemoveFavorite={removeFavorite}
        searches={savedSearches}
        onRestoreSearch={restoreSearch}
        onRemoveSearch={removeSavedSearch}
      />

      <ListingInquiryModal
        listing={inquiry?.listing ?? null}
        mode={inquiry?.mode ?? "ask"}
        onClose={() => setInquiry(null)}
      />
    </>
  );
}
